import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import PermissionLink from '../../components/PermissionLink';
import { rowClick } from '../../utils/rowNav';
import {
    listMaterials,
    deleteMaterial,
    fetchMaterialDeletePreview,
    publishMaterial,
    MaterialListItem,
    ListMaterialsParams,
} from '../../api/material';
import {
    PublishBadge,
    PublishStatusSelect,
    PublishToggle,
    isPublishedValue,
    publishedFilter,
} from '../../components/PublishBadge';
import DeleteCascadeModal from '../../components/DeleteCascadeModal';
import { alertApprovalIfPending, ApiError, imgSrc } from '../../api/request';
import MaterialExportModal from './MaterialExportModal';
import MaterialImportModal from './MaterialImportModal';
import BundleImportModal from '../../components/BundleImportModal';
import ImageLightbox from '../../components/ImageLightbox';
import ColorSwatch from '../../components/ColorSwatch';
import Pagination from '../../components/Pagination';
import BatchActions, {
    HeaderCheckbox,
    RowCheckbox,
} from '../../components/BatchActions';
import { useBatchSelect } from '../../utils/useBatchSelect';
import { useSyncQueryToUrl } from '../../utils/urlState';
import { CapBadgesCell } from '../../components/CapTypeBadges';
import { i18nText } from '../../utils/i18n';
import {
    useCategoryTree,
    useCategoryZh,
} from '../../components/MaterialFieldInputs';
import { useDictText } from '../../components/DictSelect';
import { useCan } from '../../store';
import SearchSelect from '../../components/SearchSelect';
import { zhOptions } from '../../utils/searchOptions';
import { YES_NO_OPTS } from './selectOptions';

const PAGE_SIZE = 20;

function MaterialList() {
    const canCreate = useCan('material', 'create');
    const canUpdate = useCan('material', 'update');
    const canDelete = useCan('material', 'delete');
    // 字典受控列展示中文名, 不裸显 code (颜色存 BLACK/GRAY 等枚举; 工艺/表面同为字典类目)
    const colorText = useDictText('color');
    const craftText = useDictText('craft');
    const surfaceText = useDictText('surface');
    const history = useHistory();
    const [showExport, setShowExport] = useState(false);
    const [showImport, setShowImport] = useState(false);
    const [showBundle, setShowBundle] = useState(false);
    const [zoomSrc, setZoomSrc] = useState<string | null>(null);
    // 列表状态 (页码/筛选) 同步进 URL query: 进详情后浏览器后退可还原到原页码 (2026-07-30)
    const location = useLocation();
    const initQ = new URLSearchParams(location.search);
    // input = 输入框当前值; keyword = 已提交、驱动 API 的查询词. 只在点"查询"/回车时把 input 提交进 keyword.
    const [input, setInput] = useState(initQ.get('keyword') || '');
    const [keyword, setKeyword] = useState(initQ.get('keyword') || '');
    // 表头筛选: 一级/二级分类, 变更即时生效并回到第 1 页
    const [type1, setType1] = useState(initQ.get('type1') || '');
    const [type2, setType2] = useState(initQ.get('type2') || '');
    // 发布状态筛选 (2026-09-03): '' 全部 / '2' 已发布 / '1' 未发布
    // 发布门禁 (2026-09-11 三档角色): 有材料 publish 权的人 (admin / 材料管理员) 服务端返回全量,
    // 其他人只返回已发布行, 筛选下拉对其无意义, 不渲染. 与后端 publishgate.ForAdminAPI 同口径.
    const isAdmin = useCan('material', 'publish');
    // 没有任何写权 → 整列「操作」不渲染, 点行进详情 (2026-09-12)
    const canManage = canUpdate || canDelete || isAdmin;
    const [publishStatus, setPublishStatus] = useState(
        initQ.get('publishStatus') || '',
    );
    // 是否有贴图筛选 (2026-10-08): '' 全部 / 'yes' 有 / 'no' 无
    const [texture, setTexture] = useState(initQ.get('texture') || '');
    const categoryTree = useCategoryTree();
    const { cat1Zh, cat2Zh } = useCategoryZh();
    const [page, setPage] = useState(Number(initQ.get('page')) || 1);
    const [list, setList] = useState<MaterialListItem[]>([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const load = useCallback(async (params: ListMaterialsParams) => {
        setLoading(true);
        setError('');
        try {
            const res = await listMaterials(params);
            setList(res.list || []);
            setTotal(res.totalCount);
        } catch (e) {
            const msg =
                e instanceof ApiError ? `${e.code}: ${e.message}` : String(e);
            setError(msg);
            setList([]);
            setTotal(0);
        } finally {
            setLoading(false);
        }
    }, []);

    const reload = useCallback(
        () =>
            load({
                keyword,
                type1,
                type2,
                isPublished: publishedFilter(publishStatus),
                hasTexture: texture === '' ? undefined : texture === 'yes',
                page,
                pageSize: PAGE_SIZE,
            }),
        [load, keyword, type1, type2, publishStatus, texture, page],
    );
    useEffect(() => {
        reload();
    }, [reload]);

    useSyncQueryToUrl('/materials', {
        keyword,
        type1,
        type2,
        publishStatus,
        texture,
        page,
    });

    const onSearch = (e: React.FormEvent) => {
        e.preventDefault();
        const next = input.trim();
        if (next === keyword && page === 1) {
            // 同一个查询词重复点 → 强制 reload (用户可能想刷新数据)
            reload();
            return;
        }
        setKeyword(next);
        setPage(1);
    };

    // 登录即可删: 统一走审批工单 (数据未变, load 只是回读原列表).
    // 硬删语义 (2026-08-21): 删除前弹关联数据预检, 勾选级联项随工单进审批.
    // 已发布行的编码集合: 批量发布/取消发布据此只提交状态需要变的项 (逐项 find 是 O(n²))
    const publishedKeys = useMemo(
        () =>
            new Set(
                list
                    .filter((m) => isPublishedValue(m.isPublished))
                    .map((m) => m.xcode),
            ),
        [list],
    );

    // 批量操作 (2026-09-10): 选中只在当前页有效, 换页/改筛选即清空 (见 useBatchSelect)
    const sel = useBatchSelect(
        list.map((m) => m.xcode),
        [page, keyword, type1, type2, publishStatus].join('|'),
    );

    const [delTarget, setDelTarget] = useState<MaterialListItem | null>(null);
    const onDelete = (m: MaterialListItem) => setDelTarget(m);

    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

    return (
        <div className="space-y-5">
            {/* 页头 + 筛选条版式与「设备管理」一致: 上排说明与操作按钮, 下排 card 内带标签的筛选字段.
                标题不再重复 (面包屑已有「材料主数据 / 材料库」). */}
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="text-xs text-slate-500">
                    以材质编码 (xcode) 为主键, 统一检索
                </div>
                {/* ml-auto: 窗口窄到按钮组换行时, justify-between 会把它甩到左边, 这里强制贴右 */}
                <div className="ml-auto flex flex-wrap justify-end gap-2">
                    {/* 批量操作放最前: 它是"选中行之后才亮"的一组, 摆在导入导出中间
                        会让人以为跟导入有关; 放头部与左侧的勾选列在同一视线上. */}
                    <BatchActions
                        resource="material"
                        entityLabel="材料"
                        deleteKeyField="xcode"
                        publishable
                        publishedOf={(x) => publishedKeys.has(x)}
                        selected={sel.selectedKeys}
                        onClear={sel.clear}
                        onDone={reload}
                    />
                    <button
                        type="button"
                        className="btn-ghost"
                        title="导出 xlsx: 先勾选要导出的材料 (支持全选)"
                        onClick={() => setShowExport(true)}
                    >
                        ⬇ 导出
                    </button>
                    <button
                        type="button"
                        className="btn-ghost"
                        title="批量导入 xlsx: 下载模板填好后上传 (普通用户走审批)"
                        onClick={() => setShowImport(true)}
                        hidden={!canUpdate}
                    >
                        ⬆ 批量导入
                    </button>
                    <button
                        type="button"
                        className="btn-ghost"
                        title="套件导入: 一份 JSON/YAML 一次创建 材料+SKU+能力位+理化+贴士+参数"
                        onClick={() => setShowBundle(true)}
                        hidden={!canUpdate}
                    >
                        ⬆ 套件导入
                    </button>
                    {/* 新建材料 = 创建向导 (2026-09-16): 材料 + SKU + 能力位 + 理化 + 贴士 + 参数
                        一次填完、一个工单、一个事务落库; 原来的新建弹窗已随本入口下线. */}
                    <button
                        type="button"
                        className="btn-primary"
                        title="分步创建: 基本信息 → 理化 → 关联数据 → 加工参数 → 预检提交"
                        onClick={() => history.push('/materials/new')}
                        hidden={!canCreate}
                    >
                        + 新建材料
                    </button>
                </div>
            </div>

            <form onSubmit={onSearch} className="card p-3">
                <div className="flex flex-wrap gap-3 items-end">
                    <div>
                        <label className="field-label">关键词</label>
                        <input
                            type="search"
                            className="inp w-64"
                            placeholder="搜 材料ID / 材质编码 / 名称 / 分类"
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            onBlur={() => {
                                // 清空后失焦直接恢复默认列表, 不必再点"查询"
                                if (input.trim() === '' && keyword !== '') {
                                    setKeyword('');
                                    setPage(1);
                                }
                            }}
                        />
                    </div>
                    <div>
                        <label className="field-label">一级分类</label>
                        <SearchSelect
                            className="w-36"
                            value={type1}
                            onChange={(v) => {
                                setType1(v);
                                setType2('');
                                setPage(1);
                            }}
                            placeholder="全部类别"
                            options={zhOptions(
                                categoryTree,
                                (t) => t.categoryCode,
                                (t) => t.nameZh,
                            )}
                            clearable
                        />
                    </div>
                    {/* 二级恒显示 (无一级时禁用): 条件渲染会让整排宽度随选择跳动 */}
                    <div>
                        <label className="field-label">二级分类</label>
                        <SearchSelect
                            className="w-36"
                            value={type2}
                            disabled={!type1}
                            onChange={(v) => {
                                setType2(v);
                                setPage(1);
                            }}
                            placeholder={type1 ? '全部二级' : '先选一级分类'}
                            options={zhOptions(
                                categoryTree.find(
                                    (t) => t.categoryCode === type1,
                                )?.children || [],
                                (c) => c.categoryCode,
                                (c) => c.nameZh,
                            )}
                            clearable
                        />
                    </div>
                    {isAdmin && (
                        <div>
                            <label className="field-label">发布状态</label>
                            <PublishStatusSelect
                                className="sel w-32"
                                value={publishStatus}
                                onChange={(v) => {
                                    setPublishStatus(v);
                                    setPage(1);
                                }}
                            />
                        </div>
                    )}
                    <div>
                        <label className="field-label">是否有贴图</label>
                        <SearchSelect
                            className="w-28"
                            value={texture}
                            onChange={(v) => {
                                setTexture(v);
                                setPage(1);
                            }}
                            placeholder="全部"
                            options={YES_NO_OPTS}
                            clearable
                        />
                    </div>
                    <button type="submit" className="btn-ghost">
                        查询
                    </button>
                    {/* 重置恒显示 (无筛选时禁用): 条件渲染会让按钮位置左右跳 */}
                    <button
                        type="button"
                        className="btn-ghost text-slate-400 disabled:opacity-40"
                        title="清空筛选"
                        disabled={
                            !type1 &&
                            !type2 &&
                            !publishStatus &&
                            !texture &&
                            !keyword
                        }
                        onClick={() => {
                            setType1('');
                            setType2('');
                            setPublishStatus('');
                            setTexture('');
                            setInput('');
                            setKeyword('');
                            setPage(1);
                        }}
                    >
                        ✕ 重置
                    </button>
                    <span className="ml-auto text-xs text-slate-400">
                        共 {total} 项
                    </span>
                </div>
            </form>

            {error && (
                <div className="card p-3 text-sm text-rose-600 border-rose-200 bg-rose-50">
                    错误: {error}
                </div>
            )}

            {/* overflow-x-auto: 列多时横向滚动, 不把最右的「操作」列裁掉 */}
            <div className="card overflow-x-auto">
                <table className="w-full">
                    <thead className="bg-slate-50">
                        <tr>
                            <th className="th w-8" hidden={!canDelete}>
                                <HeaderCheckbox hidden={!canDelete} sel={sel} />
                            </th>
                            <th className="th">主图</th>
                            <th className="th">材质编码</th>
                            <th
                                className="th"
                                title="材料ID (legacy_mid): 平台自动发号 (>=10000) 或旧库 xcs 迁移号 (<10000)"
                            >
                                主 / 副 ID
                            </th>
                            <th className="th">名称</th>
                            <th className="th">一级分类</th>
                            <th className="th">二级分类</th>
                            <th className="th">工艺 / 表面</th>
                            <th className="th">颜色</th>
                            <th className="th">适配工艺类型</th>
                            <th className="th whitespace-nowrap">是否有贴图</th>
                            <th className="th">SKU</th>
                            <th
                                className="th"
                                title="只约束 OpenAPI 对外下发 (参数行还需所属设备同时已发布); 平台内不受限"
                            >
                                发布
                            </th>
                            <th className="th" hidden={!canManage}>
                                操作
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && (
                            <tr>
                                <td
                                    className="td text-center text-slate-400"
                                    colSpan={14}
                                >
                                    加载中…
                                </td>
                            </tr>
                        )}
                        {!loading && list.length === 0 && (
                            <tr>
                                <td
                                    className="td text-center text-slate-400"
                                    colSpan={14}
                                >
                                    暂无数据
                                </td>
                            </tr>
                        )}
                        {list.map((m) => {
                            return (
                                <tr
                                    key={m.id}
                                    className="hover:bg-slate-50 cursor-pointer"
                                    title="点击查看详情"
                                    onClick={rowClick(() =>
                                        history.push(
                                            `/materials/${encodeURIComponent(m.xcode)}`,
                                        ),
                                    )}
                                >
                                    <td className="td" hidden={!canDelete}>
                                        <RowCheckbox
                                            hidden={!canDelete}
                                            sel={sel}
                                            k={m.xcode}
                                        />
                                    </td>
                                    <td className="td">
                                        {m.image ? (
                                            <img
                                                src={imgSrc(m.image)}
                                                alt={i18nText(m.name)}
                                                className="h-12 w-12 rounded border border-slate-200 object-cover cursor-zoom-in hover:ring-2 hover:ring-brand-300"
                                                onClick={() =>
                                                    setZoomSrc(imgSrc(m.image))
                                                }
                                                onError={(e) => {
                                                    (
                                                        e.currentTarget as HTMLImageElement
                                                    ).style.display = 'none';
                                                }}
                                            />
                                        ) : (
                                            <span className="inline-flex h-12 w-12 items-center justify-center rounded border border-dashed border-slate-200 bg-slate-50 text-[10px] text-slate-400">
                                                无图
                                            </span>
                                        )}
                                    </td>
                                    <td className="td font-mono text-xs whitespace-nowrap">
                                        <PermissionLink
                                            resource="material"
                                            to={`/materials/${encodeURIComponent(m.xcode)}`}
                                            className="text-brand-600 hover:underline"
                                        >
                                            {m.xcode}
                                        </PermissionLink>
                                    </td>
                                    <td className="td font-mono text-xs text-slate-500">
                                        <div>主：{m.legacyMid || '—'}</div>
                                        <div className="mt-1 break-all">
                                            副：
                                            {m.secondaryLegacyMids?.join(
                                                '、',
                                            ) || '—'}
                                        </div>
                                    </td>
                                    <td className="td">
                                        <div>{i18nText(m.name)}</div>
                                        {m.name?.en &&
                                            i18nText(m.name) !== m.name.en && (
                                                <div className="text-[11px] text-slate-400">
                                                    {m.name.en}
                                                </div>
                                            )}
                                    </td>
                                    <td className="td">
                                        {cat1Zh(m.type1) || '—'}
                                    </td>
                                    <td className="td">
                                        {cat2Zh(m.type1, m.type2) || '—'}
                                    </td>
                                    <td className="td text-xs">
                                        <div>
                                            <span className="text-slate-400 mr-1">
                                                工艺:
                                            </span>
                                            {craftText(m.craft1)}
                                            {m.craft2
                                                ? ` · ${craftText(m.craft2)}`
                                                : ''}
                                        </div>
                                        <div>
                                            <span className="text-slate-400 mr-1">
                                                表面:
                                            </span>
                                            {surfaceText(m.surface1)}
                                            {m.surface2
                                                ? ` · ${surfaceText(m.surface2)}`
                                                : ''}
                                        </div>
                                    </td>
                                    <td className="td whitespace-nowrap">
                                        <ColorSwatch
                                            hex={m.colorHex}
                                            label={
                                                m.color
                                                    ? colorText(m.color)
                                                    : ''
                                            }
                                        />
                                        {/* 第二行单独显示色值 (对齐"工艺 / 表面"两行式): 颜色名相同但 hex 不同的材料需要肉眼区分 */}
                                        <div className="font-mono text-[11px] text-slate-400">
                                            {m.colorHex || '—'}
                                        </div>
                                    </td>
                                    <td className="td">
                                        <CapBadgesCell caps={m.caps || []} />
                                    </td>
                                    <td className="td whitespace-nowrap">
                                        {m.textureUrl ? (
                                            <button
                                                type="button"
                                                className="text-brand-600 hover:underline"
                                                title="点击预览贴图"
                                                onClick={() =>
                                                    setZoomSrc(
                                                        imgSrc(m.textureUrl),
                                                    )
                                                }
                                            >
                                                是
                                            </button>
                                        ) : (
                                            <span className="text-slate-400">
                                                否
                                            </span>
                                        )}
                                    </td>
                                    <td className="td whitespace-nowrap">
                                        {m.skuCount > 0 ? (
                                            <PermissionLink
                                                resource="material"
                                                to={`/skus?xcode=${encodeURIComponent(m.xcode)}`}
                                                className="text-brand-600 hover:underline"
                                                title="在 SKU 库中查看该材质的 SKU"
                                            >
                                                {m.skuCount}
                                            </PermissionLink>
                                        ) : (
                                            m.skuCount
                                        )}
                                    </td>
                                    <td className="td whitespace-nowrap">
                                        <PublishBadge
                                            isPublished={m.isPublished}
                                            showTime={false}
                                        />
                                    </td>
                                    <td
                                        className="td whitespace-nowrap"
                                        hidden={!canManage}
                                    >
                                        <PublishToggle
                                            resource="material"
                                            published={isPublishedValue(
                                                m.isPublished,
                                            )}
                                            label={m.xcode}
                                            className={`mr-3 text-xs ${isPublishedValue(m.isPublished) ? 'text-slate-500' : 'text-emerald-600'} hover:underline`}
                                            onToggle={(p) =>
                                                publishMaterial(m.xcode, p)
                                            }
                                            onDone={reload}
                                        />
                                        <button
                                            type="button"
                                            className="btn-danger-text"
                                            title="提交后走审批"
                                            onClick={() => onDelete(m)}
                                            hidden={!canDelete}
                                        >
                                            删除
                                        </button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            <div className="flex items-center justify-between text-xs text-slate-500">
                <span>共 {total} 条</span>
                <div className="flex items-center gap-2">
                    <Pagination
                        page={page}
                        totalPages={totalPages}
                        onChange={setPage}
                    />
                </div>
            </div>

            <MaterialExportModal
                open={showExport}
                onClose={() => setShowExport(false)}
            />
            <BundleImportModal
                kind="material"
                open={showBundle}
                onClose={() => setShowBundle(false)}
                onImported={() =>
                    load({ keyword, type1, type2, page, pageSize: PAGE_SIZE })
                }
            />

            <MaterialImportModal
                open={showImport}
                onClose={() => setShowImport(false)}
                onImported={() => {
                    // 直接入库成功 (本地免审批) → 刷新列表; 审批流场景数据未变, 不会触发
                    load({ keyword, type1, type2, page, pageSize: PAGE_SIZE });
                }}
            />
            <ImageLightbox src={zoomSrc} onClose={() => setZoomSrc(null)} />

            <DeleteCascadeModal
                open={!!delTarget}
                title="删除材料"
                targetLabel={
                    delTarget
                        ? `${delTarget.xcode} ${i18nText(delTarget.name || {})}`
                        : ''
                }
                loadPreview={() =>
                    delTarget
                        ? fetchMaterialDeletePreview(delTarget.xcode)
                        : Promise.resolve([])
                }
                onClose={() => setDelTarget(null)}
                onConfirm={async (payload) => {
                    if (!delTarget) return;
                    const res = await deleteMaterial(delTarget.xcode, payload);
                    await load({
                        keyword,
                        type1,
                        type2,
                        page,
                        pageSize: PAGE_SIZE,
                    });
                    alertApprovalIfPending(res.message);
                }}
            />
        </div>
    );
}

export default MaterialList;
